import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import {
  getProductLinesFromArray,
  PRODUCT_LINES_CONTENT_MAP,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import axios from 'axios';
import { uploadFiles } from 'global/utils/uploadFiles';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { DEFAULT_RENEWAL_ENROLLMENT_OBJECT } from 'store/domain/enrollmentStore';
import { INITIAL_BI_GROUP_OBJECT } from 'store/domain/groupStore';
import { RootStore } from 'store/rootStore';
import { Domain } from 'types/domainServiceV2';
import { EmailMapping, Status } from 'types/emailService';
import {
  EmailReceiver,
  EnrollmentStage,
  EnrollmentType,
  InstalledEnrollmentType,
  InstalledProductLine,
} from 'types/enrollmentService';
import { UserType } from 'types/general';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { FetchResult } from 'apollo-link';
import { getAllDomains } from 'api/operations/domainServiceV2';
import { domainServiceHelper } from 'global/services-helpers';
import { sendRenewalDigest as requestSendRenewalDigest } from 'api/operations/groupService';
import {
  addExhibit,
  deleteEnrollments,
  triggerAllRenewal,
  triggerRenewal,
  triggerRenewalsBatch,
  updateEffectiveDate,
} from 'api/operations/enrollmentService';
import { setUserOrgAffiliation } from 'api/operations/aaaService';
import { refreshToken } from 'api/refreshToken';
import { PaginationDelegate } from 'store/general/paginationDelegate';
import { generateExhibits } from 'api/operations/proposalServiceV3';
import { imCelticsRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { BiGroup, ContactInfo, RenewalAgentOfRecord, GroupSortBy, SortOrder } from '../../../types/groupService';
import { EmailsWarningStatistic, TriggerModalType, TriggerRenewalsFilter } from './types';

export default class TriggerRenewalsStore {
  rootStore: RootStore;
  loading = false;
  domains: Domain[] = [];
  isEmailsModalOpen = false;
  isWarningModalOpen = false;
  triggerModalType: TriggerModalType | null = null;
  emailsByPeriodId: EmailMapping[] = [];
  currentPeriod: InstalledEnrollmentType = DEFAULT_RENEWAL_ENROLLMENT_OBJECT;
  // All group periods
  groupPeriods: InstalledEnrollmentType[] = [];
  currentGroup: BiGroup = INITIAL_BI_GROUP_OBJECT;

  // Pagination delegate
  public paginationDelegate: PaginationDelegate<GroupSortBy, SortOrder, TriggerRenewalsFilter>;

  // Determines whether the component should trigger an Apollo request to update group periods.
  // This indicator helps to understand when the request should be called, particularly when removing periods or updating emails statuses.
  shouldPeriodBeUpdated = false;
  // Determines whether the warning modal should be shown.
  shouldWarningModalBeShown = false;
  // Indicator to show that Period doesn't have exhibit files for each renewal product line
  missingExhibits = '';
  // Indicator to show that some of the users don't have emails and are not displayed
  missingUsersEmailsMessage = '';

  // Upload Exhibit Modal properties
  isFileUploadReset = false;
  isUploadModalOpen = false;

  // Batch apply properties
  // Array of period IDs that were selected for batch apply action
  selectedPeriods: InstalledEnrollmentType[] = [];
  // An array of groups containing the selected period IDs for the batch apply action.
  selectedGroups: BiGroup[] = [];

  // Select all properties
  isSelectAllButtonChecked = false;
  // The list of all periodIds per page for the select all functionality.
  allPeriods: InstalledEnrollmentType[] = [];

  // General methods
  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  public async getAllDomains(): Promise<void> {
    const convertedRoles = this.rootStore.userStore.userRoles.map((role) => toJS(role)).slice();
    if (imCelticsRights(convertedRoles)) {
      const res = await getAllDomains();
      runInAction(() => {
        const domains = res.data.getAllDomains;
        this.domains = domains;
        // Set current domain to the filter
        this.paginationDelegate.setFilterValue('sponsorId', this.rootStore.domainStoreV2.domainHeader.id);
      });
    } else {
      this.domains = [toJS(this.rootStore.domainStoreV2.domainHeader)];
      // Set current domain to the filter
      this.paginationDelegate.setFilterValue('sponsorId', this.rootStore.domainStoreV2.domainHeader.id);
    }
  }

  /**
   * Retrieve information about email statuses for each period.
   */
  public async getEmailMappingsByPeriodId(periodId: string): Promise<EmailMapping[]> {
    const res = await this.rootStore.emailStore.getEmailsByPeriodId(periodId);
    return res;
  }

  /**
   * Manually add the email status "NEW" to display in the layout.
   * The backend does not provide this email status by default, but it should be properly displayed in the layout.
   */
  // eslint-disable-next-line class-methods-use-this
  public getEmailsWithStatusNew(
    type: UserType,
    emailsWithStatuses: EmailMapping[],
    enrollmentPeriodId: string,
    allEmails: string[],
  ): EmailMapping[] {
    const emailsByTypeWithStatuses = emailsWithStatuses.filter((email) => email.userType === UserType[type]);
    const newEmails = allEmails
      .filter((email) => !emailsByTypeWithStatuses.map((emailInfo) => emailInfo.emailAddress).includes(email))
      .map((email) => ({
        emailId: '',
        emailAddress: email,
        enrollmentPeriodId,
        status: Status.NEW,
        userType: UserType[type],
      }));

    return newEmails;
  }

  // eslint-disable-next-line class-methods-use-this
  public getMissingProductLines(period: InstalledEnrollmentType): InstalledProductLine[] {
    return (
      period.installedProposal?.productLines.filter(
        (line) => line.exhibitUrls.length === 0 && line.type !== EnrollmentType.TERMINATED,
      ) || []
    );
  }

  /**
   * Text containing information about a missing exhibit for a period to display in a tooltip within the product lines column.
   */
  // eslint-disable-next-line class-methods-use-this
  public missingExhibitTexts(period: InstalledEnrollmentType): string {
    const missingProductLines = this.getMissingProductLines(period)
      .map((line) => toJS(line))
      .slice();
    return missingProductLines && missingProductLines.length > 0
      ? `Exhibits for: ${getProductLinesFromArray(missingProductLines.map((line) => line.id))} are missing`
      : '';
  }

  // eslint-disable-next-line class-methods-use-this
  private getMissingRenewalRates(period: InstalledEnrollmentType): InstalledProductLine[] {
    return (
      period.installedProposal?.productLines.filter(
        (line) =>
          line.type !== EnrollmentType.TERMINATED &&
          !line.products.some((product) => product.rates.some((rate) => rate.renewalRate.length > 0)),
      ) || []
    );
  }

  // eslint-disable-next-line class-methods-use-this
  private getMissingProducts(period: InstalledEnrollmentType): InstalledProductLine[] {
    return (
      period.installedProposal?.productLines.filter(
        (line) => line.type !== EnrollmentType.TERMINATED && line.products.length === 0,
      ) || []
    );
  }

  /**
   * Text containing information about a missing products or product rates to display in a tooltip within the product lines column.
   */
  // eslint-disable-next-line class-methods-use-this
  public missingProductsOrRatesTexts(
    period: InstalledEnrollmentType,
  ): { message: string; type: 'PRODUCTS' | 'RATES' } | null {
    const missingRenewalRates = this.getMissingRenewalRates(period)
      .map((line) => toJS(line))
      .slice();

    const missingProducts = this.getMissingProducts(period)
      .map((line) => toJS(line))
      .slice();

    // Missing rates for product names or missing rates for product lines
    const missingParts: string[] = [];

    if (missingProducts.length > 0) {
      missingProducts.forEach((line) => {
        if (line.products.length === 0) {
          missingParts.push(PRODUCT_LINES_CONTENT_MAP[line.id].label);
        }
      });

      return { message: `Products for: ${missingParts.join(', ')} are missing`, type: 'PRODUCTS' };
    }

    if (missingProducts.length === 0 && missingRenewalRates.length > 0) {
      missingRenewalRates.forEach((line) => {
        line.products.forEach((product) => {
          if (product.rates.some((rate) => rate.renewalRate.length === 0)) {
            missingParts.push(product.carrierProductName);
          }
        });
      });

      return { message: `Rates for: ${missingParts.join(', ')} are missing`, type: 'RATES' };
    }

    return null;
  }

  public setMissingUsersEmailsStatistic(statistic: EmailsWarningStatistic): void {
    const messageParts: string[] = [];

    // If the user type statistic is greater than 0, add the relevant part to the message
    if (statistic.ADMIN > 0) {
      messageParts.push(`Admin: ${statistic.ADMIN}`);
    }

    if (statistic.AOR > 0) {
      messageParts.push(`Agent of record: ${statistic.AOR}`);
    }

    this.missingUsersEmailsMessage =
      messageParts.length > 0 ? `Emails for some users are missing: ${messageParts.join(', ')}` : '';
  }

  // Methods for selecting all items and performing batch selections.
  public setSelectAllButtonStatus(): void {
    this.isSelectAllButtonChecked = !this.isSelectAllButtonChecked;
    const convertedAllPeriods = this.allPeriods.map((period) => toJS(period)).slice();
    if (this.isSelectAllButtonChecked) {
      this.updateSelectedPeriods(convertedAllPeriods);
      // The selectedGroups aren't updated because they are not used, so there is no need to collect them
    } else {
      this.resetSelectedPeriods();
    }
  }

  private updateSelectedPeriods(periods: InstalledEnrollmentType[]): void {
    this.selectedPeriods = periods;
  }

  /**
   * Used exclusively for collecting all periods for the select all functionality.
   * */
  public updateAllPeriodsList(periods: InstalledEnrollmentType[]): void {
    const convertedAllPeriods = this.allPeriods.map((period) => toJS(period)).slice();
    this.allPeriods = [...convertedAllPeriods, ...periods];
  }

  /**
   * Clear the information about all periods if any changes have occurred that affect the list of groups (effective date, sponsor, stage, page ...).
   * */
  public resetAllPeriods(): void {
    this.allPeriods = [];
  }

  /**
   * Clear the selected periods if any changes have occurred that affect the list of groups (such as effective date, sponsor, stage, page, etc.).
   * Uncheck the "select all" checkbox.
   * The selection of periods in the selected periods list depends on whether the checkbox in a row is checked or not.
   */
  public resetSelectedPeriods(): void {
    this.updateSelectedPeriods([]);
    this.selectedGroups = [];
    this.isSelectAllButtonChecked = false;
  }

  /**
   * Determine the checked status of the checkbox in each row for each period.
   */
  public getPeriodSelectedStatus(period: InstalledEnrollmentType): boolean {
    const convertedSelectedPeriods = this.selectedPeriods.map((selectedPeriod) => toJS(selectedPeriod)).slice();
    return convertedSelectedPeriods.some((selectedPeriod) => selectedPeriod.id === period.id);
  }

  /**
   * Updates the selected periods list based on the provided period and group.
   * If the period is already selected, it will be removed from the selected periods.
   * If the group no longer has any selected periods, it will be removed from the selected groups.
   * If the period is not selected, it will be added to the selected periods.
   * If the group was not selected earlier, it will be added to the selected groups.
   *
   * @param period - The period to be updated in the selected periods list.
   * @param group - The group associated with the period.
   */
  public updateSelectedPeriodsList(period: InstalledEnrollmentType, group: BiGroup): void {
    const isPeriodSelected = this.getPeriodSelectedStatus(period);
    const isCurrentGroupSelected = this.selectedGroups.some((selectedGroup) => selectedGroup.id === period.groupId);
    const convertedSelectedPeriods = this.selectedPeriods.map((selectedPeriod) => toJS(selectedPeriod)).slice();

    if (isPeriodSelected) {
      // Remove the period from the selected periods list
      const filteredPeriods = convertedSelectedPeriods.filter((selectedPeriod) => selectedPeriod.id !== period.id);
      this.updateSelectedPeriods(filteredPeriods);

      // Check if the group no longer has any selected periods
      const doesGroupHaveSelectedPeriods = convertedSelectedPeriods.some(
        (selectedPeriod) => selectedPeriod.groupId === group.id,
      );

      if (!doesGroupHaveSelectedPeriods) {
        // Remove the group from the selected groups if it no longer has selected periods
        this.selectedGroups = this.selectedGroups.filter((selectedGroup) => selectedGroup.id !== group.id);
      }
    } else {
      // Add the period to the selected periods list
      this.updateSelectedPeriods([...convertedSelectedPeriods, period]);

      if (!isCurrentGroupSelected) {
        // Add the group to the selected groups if it was not selected earlier
        this.selectedGroups.push(group);
      }
    }
  }

  /**
   * Trigger a request to update the group and its associated periods.
   */
  public setShouldPeriodBeUpdated(status: boolean): void {
    this.shouldPeriodBeUpdated = status;
  }

  /**
   * Store the current period for actions in the 'More' dropdown for each period
   */
  public setCurrentPeriod(period: InstalledEnrollmentType): void {
    this.currentPeriod = period;
  }

  /**
   * Stores all group periods for the 'Edit Effective Date' modal type to enable the filtering of unavailable effective dates.
   */
  public updateAllGroupPeriods(groupPeriods: InstalledEnrollmentType[]): void {
    this.groupPeriods = groupPeriods;
  }

  /**
   * Store the current group for actions in the 'More' dropdown for each period
   */
  public setCurrentGroup(group: BiGroup): void {
    this.currentGroup = group;
  }

  // Modals methods
  private setShouldWarningModalBeShown(status: boolean, period: InstalledEnrollmentType): void {
    this.shouldWarningModalBeShown = status;
    this.setCurrentPeriod(period);
  }

  private updateEmailsByPeriodId(emails: EmailMapping[]): void {
    this.emailsByPeriodId = emails;
  }

  public openEmailsModal(emails: EmailMapping[], period: InstalledEnrollmentType, group: BiGroup): void {
    this.setShouldWarningModalBeShown(false, DEFAULT_RENEWAL_ENROLLMENT_OBJECT);
    this.setCurrentGroup(INITIAL_BI_GROUP_OBJECT);
    this.isEmailsModalOpen = true;
    this.updateEmailsByPeriodId(emails);
    this.setCurrentPeriod(period);
    this.setCurrentGroup(group);
    this.missingExhibits = this.missingExhibitTexts(period);
  }

  public getUserNameByEmail(email: string, type: UserType): string {
    const convertedGroup = toJS(this.currentGroup);
    const getName = (user: ContactInfo | RenewalAgentOfRecord): string => `${user.firstName} ${user.lastName}`;

    switch (type) {
      case UserType.AOR: {
        if (convertedGroup.agentOfRecord.email === email) return getName(convertedGroup.agentOfRecord);
        const additionalAORWithCurrentEmail = convertedGroup.additionalAORs.find((aor) => aor.email === email);
        return additionalAORWithCurrentEmail ? getName(additionalAORWithCurrentEmail) : '';
      }
      case UserType.ADMIN: {
        if (convertedGroup.contactInfo.primaryEmail === email) return getName(convertedGroup.contactInfo);
        const additionalAdminWithCurrentEmail = convertedGroup.additionalContactInfos.find(
          (admin) => admin.primaryEmail === email,
        );
        return additionalAdminWithCurrentEmail ? getName(additionalAdminWithCurrentEmail) : '';
      }
      default:
        return '';
    }
  }

  public async sendEmailToSingleUser(period: InstalledEnrollmentType, emailReceiver: EmailReceiver): Promise<void> {
    this.setLoading(true);
    this.setShouldWarningModalBeShown(true, period);
    const convertedEmails = this.emailsByPeriodId.map((email) => toJS(email)).slice();
    const res = await triggerRenewal(period.id, [emailReceiver]);

    // If the user is successfully invited
    if (res.data?.triggerRenewal) {
      // for visually display the changed status
      const updatedEmails = convertedEmails.map((email): EmailMapping => {
        const { emailAddress, userType } = email;
        if (emailReceiver.email === emailAddress && emailReceiver.userType === userType) {
          return { ...email, status: Status.SENT };
        }
        return email;
      });
      this.updateEmailsByPeriodId(updatedEmails);
    }
    this.setLoading(false);
  }

  public async updateEffectiveDate(periodId: string, effectiveDate: string): Promise<void> {
    this.setLoading(true);
    const res = await updateEffectiveDate(periodId, effectiveDate);

    // If effective date was successfully updated
    if (res.data?.updateEffectiveDate) {
      toastManager.addToast('Effective date was successfully updated', ToastColorTypes.SUCCESS);
    } else {
      toastManager.addToast('Something went wrong during effective date update', ToastColorTypes.WARNING);
    }
    this.setShouldPeriodBeUpdated(true);
    this.setLoading(false);
  }

  public closeEmailsModal(): void {
    this.isEmailsModalOpen = false;
    this.updateEmailsByPeriodId([]);
    this.missingExhibits = '';
    if (this.shouldWarningModalBeShown) {
      this.openWarningModal('GET_STATUSES');
    }
  }

  public openWarningModal(modalType: TriggerModalType): void {
    this.triggerModalType = modalType;
    this.isWarningModalOpen = true;
  }

  public closeWarningModal(): void {
    this.isWarningModalOpen = false;
    this.triggerModalType = null;
  }

  public closeStatusesModal(): void {
    this.isWarningModalOpen = false;
    this.triggerModalType = null;
    this.setShouldPeriodBeUpdated(true);
  }

  public generateExhibits(): void {
    // The request may take a while, so we show an in-progress toast immediately instead of waiting for the response.
    void generateExhibits(this.paginationDelegate.filters.sponsorId, this.paginationDelegate.filters.effectiveDate);
    toastManager.addToast('Exhibits generation in progress', ToastColorTypes.SUCCESS);
  }

  // General getters for selects
  public getSponsorOptions(): OptionType[] {
    const convertedDomains = this.domains.map((domain) => toJS(domain)).slice();
    return domainServiceHelper.getSponsorOptions(convertedDomains);
  }

  // eslint-disable-next-line class-methods-use-this
  public getEffectiveDateOptions(): OptionType[] {
    const now = new Date();
    now.setDate(1);
    const options = Array.from({ length: 16 }).map((_, index) => {
      const date = new Date(now);
      date.setMonth(date.getMonth() - 3 + index);
      const month = date.toLocaleString('en-US', { month: 'long' });
      return {
        value: date.toISOString().slice(0, 10),
        label: `${month} 1, ${date.getFullYear()}`,
      };
    });

    return options;
  }

  /**
   * Determines the selectable effective dates for use in the "Edit Effective Date" modal.
   * These dates exclude any dates that are already associated with an active period.
   */
  public getAvailableEffectiveDateOptions(currentPeriod: InstalledEnrollmentType): OptionType[] {
    const convertedGroupPeriods = this.groupPeriods.map((period) => toJS(period)).slice();

    const unavailableEffectiveDates = convertedGroupPeriods
      .filter((period) => period.id !== currentPeriod.id)
      .map((period) => period.effectiveDate);

    return this.getEffectiveDateOptions()
      .map((option) => toJS(option))
      .slice()
      .filter((option) => !unavailableEffectiveDates.includes(option.value));
  }

  // eslint-disable-next-line class-methods-use-this
  public getStageOptions(): OptionType[] {
    return [
      {
        value: EnrollmentStage.INSTALLED,
        label: 'Installed',
      },
      {
        value: EnrollmentStage.RENEWAL,
        label: 'Renewal',
      },
    ];
  }

  // Trigger renewal methods
  /**
   * Trigger renewals in the emails modal.
   */
  public async triggerRenewal(
    period: InstalledEnrollmentType,
    emailReceivers: EmailReceiver[],
    sendEmail = true,
  ): Promise<void> {
    this.setLoading(true);
    this.setShouldWarningModalBeShown(true, period);
    const convertedEmails = this.emailsByPeriodId.map((email) => toJS(email)).slice();

    // Send emails false - move to renewal action
    const res = await triggerRenewal(period.id, emailReceivers, sendEmail);

    runInAction(() => {
      if (res?.data?.triggerRenewal) {
        // for visually display the changed status
        if (sendEmail) {
          const updatedEmails = convertedEmails.map((email): EmailMapping => {
            if (email.status === Status.NEW) {
              return { ...email, status: Status.SENT };
            }
            return email;
          });
          this.updateEmailsByPeriodId(updatedEmails);
        }

        this.setLoading(false);
        this.closeEmailsModal();
      }
    });
  }

  /**
   * Common method to trigger renewal and display appropriate toast messages.
   */
  private async triggerRenewals(
    triggerFunction: () => Promise<FetchResult<{ [key: string]: boolean }>>,
    triggerPropertyName: string,
    successMessage: string,
    noRenewalsMessage: string,
  ): Promise<void> {
    this.setLoading(true);
    try {
      const res = await triggerFunction();
      /**
       * Indicates whether the renewals were successfully triggered.
       * If false is returned, it means that the resulting list of periods to be included in the Renewal process is empty.
       * This can happen if none of the periods have exhibits.
       */
      const wereRenewalsTriggered = res.data?.[triggerPropertyName] || false;

      if (wereRenewalsTriggered) {
        toastManager.addToast(successMessage, ToastColorTypes.SUCCESS);
      } else {
        toastManager.addToast(noRenewalsMessage, ToastColorTypes.WARNING);
      }
      this.setShouldPeriodBeUpdated(true);
    } catch {
      toastManager.addToast('Something went wrong', ToastColorTypes.WARNING);
    } finally {
      this.setLoading(false);
    }
  }

  public async triggerSelectedRenewals(periodIds: string[]): Promise<void> {
    await this.triggerRenewals(
      () => triggerRenewalsBatch(periodIds, true),
      'triggerRenewalsBatch',
      'Renewals were successfully triggered.',
      'No suitable renewals were found for triggering. Please verify if the exhibits have been provided.',
    );
  }

  public async triggerAllRenewal(sponsorId: string, renewalEffectiveDate: string): Promise<void> {
    await this.triggerRenewals(
      () => triggerAllRenewal(sponsorId, renewalEffectiveDate, true),
      'triggerAllRenewal',
      'Renewals were successfully triggered.',
      'No suitable renewals were found for triggering. Please verify if the exhibits have been provided.',
    );
  }

  public async moveSelectedPeriodsToRenewals(periodIds: string[]): Promise<void> {
    await this.triggerRenewals(
      () => triggerRenewalsBatch(periodIds, false),
      'triggerRenewalsBatch',
      'Stages were successfully changed to Renewal.',
      'No suitable renewals were found for triggering. Please verify if the exhibits have been provided.',
    );
  }

  public async moveAllToRenewal(sponsorId: string, renewalEffectiveDate: string): Promise<void> {
    await this.triggerRenewals(
      () => triggerAllRenewal(sponsorId, renewalEffectiveDate, false),
      'triggerAllRenewal',
      'Stages were successfully changed to Renewal.',
      'No suitable renewals were found for triggering. Please verify if the exhibits have been provided.',
    );
  }

  public async sendRenewalDigest(sponsorId: string, renewalEffectiveDate: string): Promise<void> {
    this.setLoading(true);
    await requestSendRenewalDigest(sponsorId, renewalEffectiveDate)
      .then(() => {
        toastManager.addToast('Statistics was successfully sent.', ToastColorTypes.SUCCESS);
        this.setLoading(false);
      })
      .catch(() => {
        toastManager.addToast('Something went wrong', ToastColorTypes.WARNING);
        this.setLoading(false);
      });
  }

  public async deleteSelectedEnrollments(periods: InstalledEnrollmentType[]): Promise<void> {
    this.setLoading(true);
    const periodIds = periods.map((period) => period.id);
    const convertedSelectedPeriods = this.selectedPeriods.map((period) => toJS(period)).slice();
    await deleteEnrollments(periodIds)
      .then(() => {
        toastManager.addToast('Periods were successfully removed', ToastColorTypes.SUCCESS);
        // Update selected periods
        const filteredPeriods = convertedSelectedPeriods.filter((selectedPeriod) => {
          return !periodIds.includes(selectedPeriod.id);
        });
        this.updateSelectedPeriods(filteredPeriods);
        this.setShouldPeriodBeUpdated(true);
        this.setLoading(false);
      })
      .catch(() => {
        toastManager.addToast('Something went wrong', ToastColorTypes.WARNING);
        this.setLoading(false);
      });
  }

  // Upload exhibit methods
  public setUploadModalStatus(status: boolean): void {
    this.isUploadModalOpen = status;
  }

  public setIsFileUploadReset(status: boolean): void {
    this.isFileUploadReset = status;
  }

  public async uploadExhibit(
    uploadedFile: AcceptedFiles,
    periodId: string,
    productLineId: ProductLineId,
  ): Promise<void> {
    // Get the uploaded file name
    const uploadedFileName = Object.keys(uploadedFile)[0];

    this.setLoading(true);

    try {
      // Step 1: Send file names and get a list of URLs from the server
      const res = await axios({
        url: `https://${process.env.REACT_APP_API_BASE_URL}/carrierFileV2/Prod/upload?fileName=${uploadedFileName}`,
        withCredentials: true,
      });

      try {
        // Step 2: Upload the file using the obtained URL
        const fileUrl = Object.values(res.data as ArrayLike<unknown>)[0] as string;
        // Note: The file name in `uploadedFile` and `res.data` should match for the common upload function to work
        await uploadFiles(uploadedFile, { [uploadedFileName]: fileUrl }, async () => {
          // Step 3: Add the exhibit to the enrollment
          const fileNameFromBE = Object.keys(res.data as Record<string, unknown>)[0];
          await addExhibit(periodId, productLineId, fileNameFromBE);
          this.setShouldPeriodBeUpdated(true);
          toastManager.addToast('The exhibit was successfully uploaded', ToastColorTypes.SUCCESS);

          this.setLoading(false);
        });
      } catch (error) {
        // Handle errors in the uploadFiles function
        this.onUploadErrorCatch();
      }
    } catch (error) {
      // Handle errors in the first REST request
      this.onUploadErrorCatch();
    }
  }

  public async uploadAllExhibits(
    uploadedFiles: AcceptedFiles,
    sponsorId: string,
    effectiveDate: string,
  ): Promise<void> {
    this.setLoading(true);
    this.setIsFileUploadReset(false);

    // Get the uploaded files names
    const uploadedFilesNames = Object.keys(uploadedFiles);
    try {
      const res = await this.rootStore.carrierFileStore.getUploadExhibitUrls(
        uploadedFilesNames,
        sponsorId,
        effectiveDate,
      );
      await runInAction(async () => {
        await uploadFiles(uploadedFiles, res, (): void => {
          toastManager.addToast('The exhibits were successfully uploaded', ToastColorTypes.SUCCESS);
          this.setIsFileUploadReset(true);
        });
        this.setLoading(false);
      });
    } catch (error) {
      this.onUploadErrorCatch();
    }
  }

  public async viewGroupProfile(groupId: string, agencyId: string, agencyName: string): Promise<void> {
    this.setLoading(true);

    const requiredDomain = this.domains.find((domain) => domain.id === this.paginationDelegate.filters.sponsorId);
    if (!requiredDomain) {
      throw new Error(`Domain not found for sponsor ID: ${this.paginationDelegate.filters.sponsorId}`);
    }
    await setUserOrgAffiliation({
      sponsor: {
        id: requiredDomain.id,
        name: requiredDomain.name,
      },
      agency: {
        id: agencyId,
        name: agencyName,
      },
      subAgency: {},
    });

    await runInAction(async () => {
      try {
        await refreshToken();
        // Triggers header refresh when sponsor or agency details are updated
        window.location.href = `/group/${groupId}`;
      } catch (error) {
        window.location.href = `https://${process.env.REACT_APP_AUTH_FE_BASE_URL}/login`;
      }

      this.setLoading(false);
    });
  }

  private onUploadErrorCatch(): void {
    toastManager.addToast('An error occurred during the file upload process.', ToastColorTypes.WARNING);
    this.setLoading(false);
  }

  private static getDefaultEffectiveDate(): string {
    const now = new Date();
    now.setDate(1);
    return now.toISOString().slice(0, 10);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.paginationDelegate = new PaginationDelegate<GroupSortBy, SortOrder, TriggerRenewalsFilter>({
      defaultSortBy: GroupSortBy.UPDATE_TIME,
      defaultSortOrder: SortOrder.DESC,
      filters: {
        effectiveDate: TriggerRenewalsStore.getDefaultEffectiveDate(),
        sponsorId: '',
        name: '',
        stage: EnrollmentStage.INSTALLED,
      },
    });

    makeAutoObservable(this, { rootStore: false }, { autoBind: true });
  }
}
